import { BaseRvRequestV1, CalendarCommitRequestV1, CalendarDiffRequestV1, CalendarDiffV1, CalendarImportRequestV1, CalendarImportV1, CalendarV1, ChronicleQueryV1, ChronicleRowV1, ChronoPreviewRequestV1, ClockAdvanceRequestV1, ClockV1, ConstraintCreateV1, ConstraintParams, ConstraintV1, EntityStateV1, PlacementCreateV1, PlacementParams, PlacementsQueryV1, PlacementUpdateV1, PlacementV1, StateCreateV1, StateParams, StateUpdateV1, WhenPreviewV1, } from '../dto/chronology.js';
import { IdParams } from '../dto/common.js';
import { DocIdParams } from '../dto/documents.js';
import { listOf, PageQueryV1 } from '../dto/list.js';
import { BaseRvQueryV1 } from '../dto/query.js';
import { defineRoute } from '../route/define-route.js';
import { HEADER_FROM_2L_S1 } from './change-batch-senders.js';
import { EXAMPLE_IDS } from './example-ids.js';
import { EXAMPLE_CALENDAR_DIFF, EXAMPLE_CALENDAR_IMPORT, EXAMPLE_CALENDAR_V1, EXAMPLE_CHRONICLE_ROW, EXAMPLE_CLOCK, EXAMPLE_CLOCK_ADVANCED, EXAMPLE_CONSTRAINT_CREATED, EXAMPLE_FOUNDRY_CALENDAR, EXAMPLE_FOUNDRY_EXPORT, EXAMPLE_PLACEMENT, EXAMPLE_PLACEMENT_CREATED, EXAMPLE_STATE, EXAMPLE_STATE_CREATED, EXAMPLE_UNPLACED_ROW, EXAMPLE_WHEN_PREVIEW, exampleTimePoint, } from './examples-chronology.js';
/**
 * The chronology routes of exit 1b (1.0b deliverable 19): the calendar (1.L-1), and the When
 * preview, placements, constraints, the Chronicle (1.L-2 stage 1), NOW and dated states (1.L-2
 * stage 2). A stale `baseRv` or `baseVersion` answers 409 `row_conflict` with
 * `details: {current: {rv}}` (or `{version}` for the calendar), the brief's convention for every
 * row that carries one; a definition that fails `compile` answers 422 `calendar_invalid` with
 * `details` as `CalendarInvalidDetailsV1`.
 */
const ids = EXAMPLE_IDS;
const session = { auth: 'session', status: 'frozen', since: 'exit-1b' };
const calendar = { ...session, owner: '1.L-1' };
const chrono1 = { ...session, owner: '1.L-2', stage: '1' };
const chrono2 = { ...session, owner: '1.L-2', stage: '2' };
const rowConflict = { 409: ['row_conflict'] };
/**
 * CS r2b items L1 to L3 (1.L-2 deliverable 1): a trashed path, anchor or session document answers
 * 409 `document_trashed`; a write the schema admits but the rows refuse (an anchor block not in the
 * document, a field that is not a live `world_date` of its type, a second canon `born` or `died`, a
 * lag on `same_time` or `during`, a dead placement, a set point with invalid ticks) answers 422
 * `invalid_body` with `details` naming the reason.
 */
const trashedOrInvalid = { 409: ['document_trashed'], 422: ['invalid_body'] };
const basedTrashedOrInvalid = {
    409: ['row_conflict', 'document_trashed'],
    422: ['invalid_body'],
};
const basedTrashed = { 409: ['row_conflict', 'document_trashed'] };
/**
 * The calendar, or 200 with a null body while no calendar exists (before the first commit): none
 * yet is an answer, not a missing resource, so a page that reads it logs no failed request
 * (DECISIONS 567, which retired the 404 `not_found` of DECISIONS 539).
 */
export const calendarGet = defineRoute({
    id: 'calendar.get',
    method: 'GET',
    path: '/api/calendar',
    response: { kind: 'json', schema: CalendarV1.nullable() },
    reference: 'none',
    ...calendar,
    examples: { response: EXAMPLE_CALENDAR_V1 },
});
/**
 * A draft's effect on every time point, recomputed in memory; nothing is written. The implicit 404
 * `not_found` while no calendar exists.
 */
export const calendarDiff = defineRoute({
    id: 'calendar.diff',
    method: 'POST',
    path: '/api/calendar/diff',
    body: CalendarDiffRequestV1,
    response: { kind: 'json', schema: CalendarDiffV1 },
    reference: 'none',
    ...calendar,
    examples: {
        request: { body: { definition: EXAMPLE_CALENDAR_V1.definition, remap: {} } },
        response: EXAMPLE_CALENDAR_DIFF,
    },
});
/**
 * Commit a new version with its remap; ticks, spans and ranks recompute in one transaction. The
 * first commit sends `baseVersion: 0` and creates version 1 (`EXAMPLE_CALENDAR_FIRST_COMMIT`).
 */
export const calendarCommit = defineRoute({
    id: 'calendar.commit',
    method: 'PUT',
    path: '/api/calendar',
    body: CalendarCommitRequestV1,
    response: { kind: 'json', schema: CalendarV1 },
    errors: { ...rowConflict, 422: ['calendar_invalid'] },
    reference: 'none',
    ...calendar,
    examples: {
        request: {
            body: {
                baseVersion: 1,
                definition: EXAMPLE_CALENDAR_V1.definition,
                remap: {},
                note: 'The Watch reckons by the Almanac.',
            },
        },
        response: { ...EXAMPLE_CALENDAR_V1, version: 2, note: 'The Watch reckons by the Almanac.' },
        consumes: true,
    },
});
/** Read a Foundry calendar file into a definition; nothing is stored (1.K-1 deliverable 1). */
export const calendarImportFoundry = defineRoute({
    id: 'calendar.importFoundry',
    method: 'POST',
    path: '/api/calendar/import-foundry',
    body: CalendarImportRequestV1,
    response: { kind: 'json', schema: CalendarImportV1 },
    errors: { 422: ['parse_failed', 'calendar_invalid'] },
    reference: 'none',
    ...calendar,
    examples: {
        request: { body: { calendar: EXAMPLE_FOUNDRY_CALENDAR, seasonDayBase: 0 } },
        response: EXAMPLE_CALENDAR_IMPORT,
    },
});
/**
 * The calendar as a Foundry calendar file (`toFoundryCalendar`); the implicit 404 `not_found` while
 * no calendar exists.
 */
export const calendarExportFoundry = defineRoute({
    id: 'calendar.exportFoundry',
    method: 'GET',
    path: '/api/calendar/foundry',
    response: { kind: 'file' },
    reference: 'none',
    ...calendar,
    examples: { response: EXAMPLE_FOUNDRY_EXPORT },
});
/** What the When input shows before commit; a smoke session may call it. */
export const chronoPreview = defineRoute({
    id: 'chrono.preview',
    method: 'POST',
    path: '/api/chrono/preview',
    body: ChronoPreviewRequestV1,
    response: { kind: 'json', schema: WhenPreviewV1 },
    smoke: 'read',
    reference: 'read',
    ...chrono1,
    examples: {
        request: { body: { input: '12 Ember 1042', docId: ids.questMissingLamplighters.id } },
        response: EXAMPLE_WHEN_PREVIEW,
    },
});
/**
 * One document's placements (a bounded list); with `participating=true`, the live placements of
 * other documents naming it as a participant (CS r2c, item W1; 1.L-2 stage 2 answers it, and until
 * then the real server answers the document's own).
 */
export const placementsList = defineRoute({
    id: 'placements.list',
    method: 'GET',
    path: '/api/docs/:id/placements',
    params: DocIdParams,
    query: PlacementsQueryV1,
    response: { kind: 'json', schema: listOf(PlacementV1) },
    reference: 'read',
    docIds: { owner: ['params.id'] },
    ...chrono1,
    examples: {
        // No `participating`: the document's own placements, as before W1 (the parity case unchanged).
        request: { params: { id: ids.questSaltInTheWells.id }, query: {} },
        response: { items: [EXAMPLE_PLACEMENT], next: null },
    },
});
/** Place the document of the path; a participant may be a reference document. */
export const placementsCreate = defineRoute({
    id: 'placements.create',
    method: 'POST',
    path: '/api/docs/:id/placements',
    params: DocIdParams,
    body: PlacementCreateV1,
    response: { kind: 'json', schema: PlacementV1, status: 201 },
    errors: trashedOrInvalid,
    reference: 'target-ok',
    docIds: { owner: ['params.id'], target: ['body.participants[].docId'] },
    ...chrono1,
    changeBatch: true,
    changeBatchFrom: HEADER_FROM_2L_S1,
    examples: {
        request: {
            params: { id: ids.questBellsBeneathTheMarsh.id },
            body: {
                kind: 'moment',
                start: exampleTimePoint({ era: 'al', year: 1042, month: 'mist', day: 1 }, 'day', '1 Mist 1042'),
                participants: [{ docId: ids.npcPaleAbbot.id, role: 'host' }],
            },
        },
        response: EXAMPLE_PLACEMENT_CREATED,
    },
});
export const placementsUpdate = defineRoute({
    id: 'placements.update',
    method: 'PATCH',
    path: '/api/docs/:id/placements/:placementId',
    params: PlacementParams,
    body: PlacementUpdateV1,
    response: { kind: 'json', schema: PlacementV1 },
    errors: basedTrashedOrInvalid,
    reference: 'target-ok',
    docIds: { owner: ['params.id'], target: ['body.participants[].docId'] },
    ...chrono1,
    changeBatch: true,
    changeBatchFrom: HEADER_FROM_2L_S1,
    examples: {
        request: {
            params: {
                id: ids.questMissingLamplighters.id,
                placementId: ids.placementMissingLamplighters.id,
            },
            body: { baseRv: 1, participants: [{ docId: ids.npcOrlaVenn.id, role: 'giver' }] },
        },
        response: {
            ...EXAMPLE_PLACEMENT,
            id: ids.placementMissingLamplighters.id,
            documentId: ids.questMissingLamplighters.id,
            rv: 2,
            participants: [{ docId: ids.npcOrlaVenn.id, role: 'giver' }],
        },
        consumes: true,
    },
});
export const placementsRemove = defineRoute({
    id: 'placements.remove',
    method: 'DELETE',
    path: '/api/docs/:id/placements/:placementId',
    params: PlacementParams,
    query: BaseRvQueryV1,
    response: { kind: 'empty' },
    errors: basedTrashed,
    reference: 'deny',
    docIds: { owner: ['params.id'] },
    ...chrono1,
    changeBatch: true,
    changeBatchFrom: HEADER_FROM_2L_S1,
    examples: {
        request: {
            params: { id: ids.loreCoronation.id, placementId: ids.placementCoronationAlternate.id },
            query: { baseRv: 1 },
        },
        response: null,
        consumes: true,
    },
});
/**
 * Order two placements' edges; the repository upserts on (before, after, relation). A side may name
 * a document instead of a placement (`beforeDocId`, `afterDocId`): its anchor placement.
 */
export const constraintsCreate = defineRoute({
    id: 'constraints.create',
    method: 'POST',
    path: '/api/docs/:id/constraints',
    params: DocIdParams,
    body: ConstraintCreateV1,
    response: { kind: 'json', schema: ConstraintV1, status: 201 },
    errors: trashedOrInvalid,
    reference: 'deny',
    docIds: { owner: ['params.id', 'body.beforeDocId', 'body.afterDocId'] },
    ...chrono1,
    examples: {
        request: {
            params: { id: ids.questAbbotsBargain.id },
            // Both sides by document (L1), so the reference sweep reaches both pointers: each quest
            // holds one placement, its anchor (no other example in the parity group adds one), so the answer orders the same two placements.
            body: {
                beforeDocId: ids.questSunkenChapel.id,
                afterDocId: ids.questAbbotsBargain.id,
                relation: 'before',
            },
        },
        response: EXAMPLE_CONSTRAINT_CREATED,
    },
});
export const constraintsRemove = defineRoute({
    id: 'constraints.remove',
    method: 'DELETE',
    path: '/api/docs/:id/constraints/:constraintId',
    params: ConstraintParams,
    query: BaseRvQueryV1,
    response: { kind: 'empty' },
    errors: basedTrashed,
    reference: 'deny',
    docIds: { owner: ['params.id'] },
    ...chrono1,
    examples: {
        request: {
            params: {
                id: ids.questSunkenChapel.id,
                constraintId: ids.constraintSunkenChapelAfterBells.id,
            },
            query: { baseRv: 1 },
        },
        response: null,
        consumes: true,
    },
});
/** The Chronicle: placements by in-world rank, within `[from, to)` when given. */
export const chronicleList = defineRoute({
    id: 'chronicle.list',
    method: 'GET',
    path: '/api/chronicle',
    query: ChronicleQueryV1,
    response: { kind: 'json', schema: listOf(ChronicleRowV1) },
    reference: 'none',
    ...chrono1,
    examples: {
        request: { query: { density: 'month', limit: 50 } },
        response: { items: [EXAMPLE_CHRONICLE_ROW], next: null },
    },
});
/** The Unplaced tray: undated placements with no constraint. */
export const chronicleUnplaced = defineRoute({
    id: 'chronicle.unplaced',
    method: 'GET',
    path: '/api/chronicle/unplaced',
    query: PageQueryV1,
    response: { kind: 'json', schema: listOf(ChronicleRowV1) },
    reference: 'none',
    ...chrono1,
    examples: {
        request: { query: { limit: 50 } },
        response: { items: [EXAMPLE_UNPLACED_ROW], next: null },
    },
});
/**
 * NOW, or 200 with a null body while no clock exists (before the first set), as `calendar.get`
 * answers no calendar (DECISIONS 567).
 */
export const clockGet = defineRoute({
    id: 'clock.get',
    method: 'GET',
    path: '/api/clock',
    response: { kind: 'json', schema: ClockV1.nullable() },
    reference: 'none',
    ...chrono2,
    examples: { response: EXAMPLE_CLOCK },
});
/** Undo one advance; the later ones re-fold (baseline section 3, NOW). */
export const clockUndo = defineRoute({
    id: 'clock.undo',
    method: 'POST',
    path: '/api/clock/advances/:id/undo',
    params: IdParams,
    body: BaseRvRequestV1,
    response: { kind: 'json', schema: ClockV1 },
    errors: rowConflict,
    reference: 'none',
    ...chrono2,
    examples: {
        // CS r5b: demo@p2b's two-hour advance (session 2's promoted time capture) moved NOW, so the
        // clock is at rv 2 and the advance can be undone.
        request: { params: { id: ids.clockAdvanceToUndo.id }, body: { baseRv: 2 } },
        response: EXAMPLE_CLOCK,
        consumes: true,
    },
});
/**
 * Move NOW by a delta or to a set point; a session is never a reference document. The first set
 * sends `baseRv: 0` and creates the clock at rv 1 (`EXAMPLE_CLOCK_FIRST_SET`).
 */
export const clockAdvance = defineRoute({
    id: 'clock.advance',
    method: 'POST',
    path: '/api/clock/advances',
    body: ClockAdvanceRequestV1,
    response: { kind: 'json', schema: ClockV1 },
    errors: basedTrashedOrInvalid,
    reference: 'deny',
    docIds: { owner: ['body.sessionId'] },
    ...chrono2,
    examples: {
        request: {
            body: {
                kind: 'delta',
                deltaN: 8,
                deltaUnit: 'hour',
                reason: 'long_rest',
                sessionId: ids.sessionSunkenChapel.id,
                // CS r5b: demo@p2b's advance leaves the clock at rv 2.
                baseRv: 2,
            },
        },
        response: EXAMPLE_CLOCK_ADVANCED,
        consumes: true,
    },
});
/** One document's dated states (a bounded list). */
export const statesList = defineRoute({
    id: 'states.list',
    method: 'GET',
    path: '/api/docs/:id/states',
    params: DocIdParams,
    response: { kind: 'json', schema: listOf(EntityStateV1) },
    reference: 'read',
    docIds: { owner: ['params.id'] },
    ...chrono2,
    examples: {
        request: { params: { id: ids.npcOldSef.id } },
        response: { items: [EXAMPLE_STATE], next: null },
    },
});
export const statesCreate = defineRoute({
    id: 'states.create',
    method: 'POST',
    path: '/api/docs/:id/states',
    params: DocIdParams,
    body: StateCreateV1,
    response: { kind: 'json', schema: EntityStateV1, status: 201 },
    errors: trashedOrInvalid,
    reference: 'deny',
    docIds: { owner: ['params.id'] },
    ...chrono2,
    examples: {
        request: {
            params: { id: ids.npcOldSef.id },
            body: {
                fieldDefId: ids.fieldNpcStanding.id,
                value: 'Ally',
                from: EXAMPLE_STATE_CREATED.from,
            },
        },
        response: EXAMPLE_STATE_CREATED,
    },
});
export const statesUpdate = defineRoute({
    id: 'states.update',
    method: 'PATCH',
    path: '/api/docs/:id/states/:stateId',
    params: StateParams,
    body: StateUpdateV1,
    response: { kind: 'json', schema: EntityStateV1 },
    errors: basedTrashedOrInvalid,
    reference: 'deny',
    docIds: { owner: ['params.id'] },
    ...chrono2,
    examples: {
        request: {
            params: { id: ids.npcOldSef.id, stateId: ids.stateOldSefStanding.id },
            body: { baseRv: 1, note: 'Since the wells ran salt.' },
        },
        response: { ...EXAMPLE_STATE, rv: 2, note: 'Since the wells ran salt.' },
        consumes: true,
    },
});
//# sourceMappingURL=chronology.js.map